import '@/app.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { RouterProvider } from 'react-router';

import { BackendProvider, InstanceProvider, ShadowRootProvider } from '@/api/evo';
import { queryClient } from '@/lib/query-client';
import { Locale } from '@/locale/locale';
import { hashRouter } from '@/routes';

/**
 * The application root for `@rotaready/template-builder-evo`.
 *
 * Both build outputs render this:
 *   - `src/main.tsx`           → mounts it into `#root` for the SPA bundle
 *                                 (`yarn dev:isolate` + future Evo MFE host)
 *   - `src/library-entry.ts`   → re-exports it for the library bundle
 *                                 (consumed by WFM via `@rotaready/template-builder-evo`)
 *
 * Provider stack (Phase 2, per Plan 1 Task 2.15):
 *   ShadowRootProvider     — gives Portal-using EDS components a container.
 *                            `shadowRoot={null}` keeps the light-DOM path —
 *                            production Evo hosts may pass an actual root.
 *   InstanceProvider       — env/geography/instance-id wiring from evo/api.
 *                            `instanceId="dev"` matches the harness's
 *                            dev-shim instance label.
 *   BackendProvider        — REST base URL + bearer token via evo/api.
 *   QueryClientProvider    — TanStack Query, with conservative defaults
 *                            from `@/lib/query-client`.
 *   Locale                 — Lingui provider with host-language listener.
 *   RouterProvider         — declarative `createHashRouter` from `@/routes`.
 *
 * Toast wiring (D17 — `contexts/toast-context` + `components/toast-container`)
 * is added in Phase 3's shell-blocks pass. Phase 2 ships without toasts;
 * landing/wizard interactions don't surface them yet.
 */
export function TemplateBuilderApp() {
  return (
    <ShadowRootProvider shadowRoot={null}>
      <InstanceProvider instanceId="dev">
        <BackendProvider>
          <QueryClientProvider client={queryClient}>
            <Locale>
              {/* RouterProvider's `fallbackElement` prop was dropped in RR7. The
                  `AppShellLoading` component below is wired onto route-level
                  `hydrateFallbackElement` (or `loader` + `HydrateFallback`) when
                  Phase 4 introduces real route data loaders. */}
              <RouterProvider router={hashRouter} />
            </Locale>
          </QueryClientProvider>
        </BackendProvider>
      </InstanceProvider>
    </ShadowRootProvider>
  );
}

/**
 * Loading state rendered as `RouterProvider`'s `fallbackElement` (the
 * declarative-mode equivalent of framework mode's HydrateFallback per D19).
 */
export function AppShellLoading(): ReactNode {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

// Re-exported from its own module so the route tree (`@/routes`) can wire it
// as an `ErrorBoundary` without a circular import back into this file.
export { RootErrorBoundary } from '@/components/root-error-boundary';
